import { Alert, CopyValue, TextTooltip } from '@components'
import { IPurchaseInvoice } from '@query'
import { formatDate, formatEurosWithOpts } from '@utils'
import React, { useState } from 'react'
import QRCode from 'react-qr-code'
import { useTranslation } from 'react-i18next'
import { FaChevronDown, FaTriangleExclamation } from 'react-icons/fa6'
import styled from 'styled-components'

interface Props {
  invoice: IPurchaseInvoice
  showImportNotice?: boolean
}

export const PurchaseInvoicePaymentInfo: React.FC<Props> = ({ invoice, showImportNotice }) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(!invoice.is_paid)
  const remittance = invoice.reference || invoice.message || ''
  const remittanceLabel = invoice.reference
    ? t('invoicing.purchaseInvoiceDetails.reference')
    : t('invoicing.purchaseInvoiceDetails.message')

  return (
    <Section>
      <ToggleButton type="button" onClick={() => setIsOpen(open => !open)}>
        <ToggleLabel>{t('invoicing.purchaseInvoiceDetails.paymentInfo')}</ToggleLabel>
        <ChevronWrapper $isOpen={isOpen}>
          <FaChevronDown />
        </ChevronWrapper>
      </ToggleButton>

      {isOpen && (
        <>
          {showImportNotice && (
            <Alert
              type="warning"
              description={t('invoicing.purchaseInvoiceDetails.importWarning')}
              marginBottom="0.6rem"
            />
          )}
          <Grid>
            <Field>
              <FieldLabel>{t('invoicing.purchaseInvoiceDetails.iban')}</FieldLabel>
              <CopyValue value={invoice.sender_bank_account} />
            </Field>

            {invoice.sender_bank_bic && (
              <Field>
                <FieldLabel>{t('invoicing.purchaseInvoiceDetails.bic')}</FieldLabel>
                <CopyValue value={invoice.sender_bank_bic} />
              </Field>
            )}

            <Field $fullWidth>
              <FieldLabel>{t('invoicing.purchaseInvoiceDetails.receiver')}</FieldLabel>
              <CopyValue value={invoice.sender_name} />
            </Field>

            <Field>
              <FieldLabel>{t('invoicing.purchaseInvoiceDetails.dueDate')}</FieldLabel>
              <CopyValue value={invoice.due_date ? formatDate(invoice.due_date) : ''} />
            </Field>

            <Field>
              <FieldLabel>{t('invoicing.purchaseInvoiceDetails.amount')}</FieldLabel>
              <CopyValue
                value={formatEurosWithOpts(invoice.amount, {
                  withEuroSymbol: false,
                  inCents: false
                })}
              />
            </Field>

            <Field $fullWidth>
              <FieldLabel>
                {remittanceLabel}
                {!remittance && (
                  <MissingDataAlert
                    tooltip={t('invoicing.purchaseInvoiceDetails.missingRemittance')}
                  />
                )}
              </FieldLabel>
              <CopyValue value={remittance} />
            </Field>

            {invoice.virtuaaliviivakoodi && (
              <Field $fullWidth>
                <FieldLabel>{t('invoicing.purchaseInvoiceDetails.virtuaaliviivakoodi')}</FieldLabel>
                <BarcodeRow>
                  <QRCodeWrapper>
                    <QRCode
                      value={invoice.virtuaaliviivakoodi}
                      size={72}
                      bgColor="#ffffff"
                      fgColor="#000000"
                      title="Maksa viivakoodilla"
                    />
                  </QRCodeWrapper>
                  <CopyValue value={invoice.virtuaaliviivakoodi} />
                </BarcodeRow>
              </Field>
            )}
          </Grid>
        </>
      )}
    </Section>
  )
}

const MissingDataAlert: React.FC<{ tooltip: string }> = ({ tooltip }) => {
  return (
    <TextTooltip tooltip={tooltip} placement="bottom-start">
      <StyledWarningIcon />
    </TextTooltip>
  )
}

const Section = styled.div`
  width: 100%;
`

const ToggleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: transparent;
  border: none;
  padding: 0 0 0.75rem;
  cursor: pointer;
`

const ToggleLabel = styled.span`
  color: ${({ theme }) => theme.colors.iconGray};
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`

const ChevronWrapper = styled.span<{ $isOpen: boolean }>`
  display: inline-flex;
  color: ${({ theme }) => theme.colors.iconGray};
  transform: rotate(${({ $isOpen }) => ($isOpen ? 180 : 0)}deg);
  transition: transform 0.15s ease;

  svg {
    width: 0.75rem;
    height: 0.75rem;
  }
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 1.25rem;
  row-gap: 1rem;

  @container invoice-detail (max-width: 36rem) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 48em) {
    grid-template-columns: 1fr;
  }
`

const Field = styled.div<{ $fullWidth?: boolean }>`
  grid-column: ${({ $fullWidth }) => ($fullWidth ? '1 / -1' : 'auto')};
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const FieldLabel = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.iconGray};
`

const BarcodeRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md}rem;
  min-width: 0;

  @container invoice-detail (max-width: 36rem) {
    flex-direction: column;
    align-items: stretch;
  }

  @media (max-width: 48em) {
    flex-direction: column;
    align-items: stretch;
  }
`

const QRCodeWrapper = styled.div`
  flex-shrink: 0;
  padding: ${({ theme }) => theme.spacing.xs}rem;
  border: 1px solid ${({ theme }) => theme.colors.neutralGray};
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  background: ${({ theme }) => theme.colors.neutralWhite};
`

const StyledWarningIcon = styled(FaTriangleExclamation)`
  color: ${({ theme }) => theme.colors.warning};
  height: ${({ theme }) => theme.iconSize.xs}rem;
  width: ${({ theme }) => theme.iconSize.xs}rem;
`
